import React, { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';
import { useAuth } from '../context/AuthContext';
import { C, Field, TextInput, SubmitButton, Banner } from './FormKit';

function fmtMoney(n) {
  if (n == null) return '—';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// One step of the billing pair: either already done (green, inert) or an action
// button. Rendered for payment and challan independently so neither gates the
// other — customers regularly pay before the government challan is issued.
function StepRow({ label, done, doneText, actionText, onClick, busy, color, readOnly, readOnlyText }) {
  if (done) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#EBF7EE', border: `1px solid ${C.green}`, borderRadius: 10,
        padding: '13px 16px', color: '#1B5E20', fontSize: 14, fontWeight: 600,
      }}>
        <span style={{ fontSize: 15 }}>✓</span>
        <span>{doneText}</span>
      </div>
    );
  }
  // Outstanding, but owned by the other counter — shown for context, not actionable.
  if (readOnly) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: C.panel2, border: `1px dashed ${C.border}`, borderRadius: 10,
        padding: '13px 16px', color: C.muted, fontSize: 13,
      }}>
        <span style={{ fontSize: 14 }}>○</span>
        <span>{readOnlyText}</span>
      </div>
    );
  }
  return (
    <div>
      <div style={{
        fontFamily: C.mono, fontSize: 10, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: C.muted, marginBottom: 6,
      }}>{label} — pending</div>
      <SubmitButton onClick={onClick} disabled={busy} color={color}>{actionText}</SubmitButton>
    </div>
  );
}

// Billing station, used by BOTH counters — the challan desk and the payment
// desk — with `mode` deciding which step this instance owns. Shared rather than
// duplicated because the search/select half is identical; only the queue filter
// and the available action differ.
//
// Each mode shows ONLY its own queue:
//   challan → vehicles with no challan recorded yet (whether or not they've paid)
//   payment → vehicles that haven't paid yet (whether or not a challan exists)
// Those two queues overlap on vehicles where neither step is done — correctly,
// since both desks still need to act on them.
//
// The vehicle leaves for the exit gate once both are done, in whichever order
// they happened; see ChallanController for the ordering and price-freezing rules.
export default function ChallanForm({ mode = 'challan' }) {
  const { user, logout } = useAuth();
  const isPaymentDesk = mode === 'payment';

  const [vehicles, setVehicles] = useState([]);
  const [selTx, setSelTx]       = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  // What the counter will actually collect. Pre-filled with the calculated
  // figure and editable, because some customers are on an agreed rate.
  const [amountInput, setAmountInput] = useState('');

  const selected = vehicles.find(v => v.transactionId === selTx);

  // The queue now carries vehicles that have not finished weighing, so the desk
  // can see where one got stuck instead of simply not finding it. They are
  // counted apart from the billable ones — "6 waiting" meaning one actionable
  // vehicle and five the weighbridge still owns would be worse than no count.
  // billable is undefined against an older backend; only an explicit false
  // blocks, so the form keeps working through a deploy.
  const isBlocked = v => v.billable === false;
  const blockedCount = vehicles.filter(isBlocked).length;
  const readyCount   = vehicles.length - blockedCount;

  const loadVehicles = useCallback(async () => {
    try {
      const rows = await gateApi.getChallanPending();
      setVehicles(rows.filter(v => (mode === 'payment' ? !v.paymentReceived : !v.challanGenerated)));
    } catch { /* keep last list if fetch fails */ }
  }, [mode]);

  useEffect(() => {
    loadVehicles();
    const interval = setInterval(loadVehicles, 10000);
    return () => clearInterval(interval);
  }, [loadVehicles]);

  useEffect(() => {
    function onClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = vehicles.filter(v =>
    v.licensePlate?.toUpperCase().includes(searchQuery.toUpperCase().trim())
  );

  const calculatedDue = selected ? (selected.chargedAmount ?? selected.quotedAmount) : null;
  const amountDiffers = calculatedDue != null
    && amountInput !== ''
    && Math.abs(parseFloat(amountInput) - Number(calculatedDue)) > 0.005;

  function selectVehicle(v) {
    setSelTx(v.transactionId);
    setSearchQuery(v.licensePlate || '');
    setShowDropdown(false);
    const due = v.chargedAmount ?? v.quotedAmount;
    setAmountInput(due != null ? String(due) : '');
    setResult(null);
  }

  function clearSelection() {
    setSelTx(''); setSearchQuery(''); setAmountInput('');
    setResult(null);
  }

  // Both steps are independent and can run in either order. Whichever completes
  // second sends the vehicle to the exit gate — the backend decides that, and
  // reports it back as status, so this doesn't try to work it out itself.
  async function generateChallan() {
    if (!selTx) return;
    setBusy(true); setResult(null);
    try {
      const r = await gateApi.generateChallan(selTx);
      const done = r.status === 'PENDING_EXIT';
      setResult({
        ok: true,
        msg: `${r.plate} — challan ${r.challanNumber} recorded.` +
             (done ? ' Payment already taken, sent to the exit gate.' : ' Still awaiting payment.'),
      });
      if (done) clearSelection();
      await loadVehicles();
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, msg: d?.message || d?.error || 'Could not record the challan.' });
    } finally {
      setBusy(false);
    }
  }

  async function markPaid() {
    if (!selTx) return;
    setBusy(true); setResult(null);
    try {
      const typed = parseFloat(amountInput);
      const amount = Number.isFinite(typed) ? typed : null;
      const r = await gateApi.markChallanPaid(selTx, amount);
      const done = r.status === 'PENDING_EXIT';
      setResult({
        ok: true,
        msg: `${r.plate} — payment recorded.` +
             (done ? ' Sent to the exit gate.' : ' Still awaiting the challan.'),
      });
      if (done) clearSelection();
      await loadVehicles();
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, msg: d?.message || d?.error || 'Could not mark payment received.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 22px', borderBottom: `1px solid ${C.border}`, background: C.panel,
      }}>
        <span style={{
          fontFamily: C.mono, fontSize: 13, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: C.muted,
        }}>BSC Gate · {isPaymentDesk ? 'Payment' : 'Challan'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 12, color: C.faint }}>{user?.fullName}</span>
          <button onClick={logout} style={{
            padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
            background: '#fff', color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '28px 22px 80px' }}>
        {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

        <Field label="Find vehicle"
               hint={vehicles.length
                 ? `${readyCount} ready for ${isPaymentDesk ? 'payment' : 'a challan'}`
                   + (blockedCount ? ` · ${blockedCount} still at the weighbridge` : '')
                 : 'No vehicles currently waiting'}>
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={vehicles.length ? 'Type to search number plate…' : 'none waiting'}
              disabled={!vehicles.length}
              value={searchQuery}
              onFocus={() => setShowDropdown(true)}
              onChange={(e) => { setSearchQuery(e.target.value); setSelTx(''); }}
              style={{
                width: '100%', boxSizing: 'border-box', background: C.panel2,
                border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
                padding: '14px 16px', fontSize: 16, fontFamily: C.mono, outline: 'none',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}
            />
            {showDropdown && vehicles.length > 0 && (
              <div style={{
                position: 'absolute', top: '102%', left: 0, right: 0,
                background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
                maxHeight: 260, overflowY: 'auto', zIndex: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: '6px 0',
              }}>
                {filtered.length > 0 ? filtered.map(v => (
                  <div
                    key={v.transactionId}
                    onClick={() => selectVehicle(v)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', fontSize: 14,
                      fontFamily: C.sans, color: C.text, display: 'flex',
                      justifyContent: 'space-between', transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.panel2; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontFamily: C.mono, fontWeight: 700,
                                   color: isBlocked(v) ? C.muted : C.text }}>{v.licensePlate}</span>
                    {/* Where it is beats what it owes: a vehicle the weighbridge
                        still has cannot be billed, so its stage is the only
                        useful thing to show against it. */}
                    <span style={{ color: isBlocked(v) ? C.amber : C.muted, fontSize: 12,
                                   fontWeight: isBlocked(v) ? 600 : 400 }}>
                      {isBlocked(v) ? v.stageLabel
                        : v.challanGenerated && !v.paymentReceived ? 'Needs payment'
                        : v.paymentReceived && !v.challanGenerated ? 'Paid · needs challan'
                        : 'Needs challan + payment'}
                    </span>
                  </div>
                )) : (
                  <div style={{ padding: '14px 16px', color: C.faint, fontSize: 13, fontFamily: C.sans }}>
                    No matching vehicles found
                  </div>
                )}
              </div>
            )}
          </div>
        </Field>

        {selected && (
          <div style={{
            background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: '20px 22px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, fontSize: 13, marginBottom: 16 }}>
              <span style={{ color: C.muted }}>Material:&nbsp;
                <span style={{ color: C.text }}>{selected.material || '—'}</span>
              </span>
              <span style={{ color: C.muted }}>Net weight:&nbsp;
                <span style={{ color: C.text, fontFamily: C.mono }}>
                  {selected.netWeight != null ? `${selected.netWeight} t` : '—'}
                </span>
              </span>
              {selected.chargedPricePerTonne && (
                <span style={{ color: C.muted }}>Rate:&nbsp;
                  <span style={{ color: C.text, fontFamily: C.mono }}>
                    {fmtMoney(selected.chargedPricePerTonne)}/t
                  </span>
                </span>
              )}
            </div>

            {/* Why this one cannot be billed, and what to do about it. The desk
                has no way to fix a missing weighing itself — the action is a
                telephone call to the weighbridge — so the notice names the
                stage rather than offering a control. */}
            {isBlocked(selected) && (
              <div style={{
                background: '#FEF6E7', border: `1px solid ${C.amber}`, borderRadius: 10,
                padding: '14px 16px', marginBottom: 18,
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>
                  {selected.stageLabel}
                </div>
                <div style={{ fontSize: 13, color: '#92400E', lineHeight: 1.45 }}>
                  {selected.lifecycleStatus === 'PENDING_CHALLAN'
                    ? 'This vehicle reached billing but has no weighbridge reading on record. Ask the weighbridge to enter it before billing.'
                    : 'The weighbridge has not finished with this vehicle. Call the weighbridge to get the weight entered, then it will appear here ready to bill.'}
                </div>
              </div>
            )}

            {/* Amount owed. Shown from the moment the vehicle is weighed, before
                either step runs — the counter often takes cash first. Once either
                step completes the rate is frozen, so this figure stops moving. */}
            {isPaymentDesk && !selected.paymentReceived ? (
              // Editable at the payment counter: the calculated figure is only a
              // starting point, since some customers are on an agreed rate.
              <div style={{ marginBottom: 18 }}>
                <Field label="Amount to collect (₹)"
                       hint={calculatedDue != null && amountDiffers
                         ? `Calculated ${fmtMoney(calculatedDue)} — this change will be recorded`
                         : 'Calculated from net weight × rate. Change it if the customer is on an agreed rate.'}>
                  <TextInput value={amountInput} onChange={setAmountInput} type="number" mono
                             placeholder="0.00" />
                </Field>
              </div>
            ) : (
              <div style={{
                background: C.panel2, borderRadius: 10, padding: '16px 18px', marginBottom: 18,
              }}>
                <div style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>
                  {selected.challanGenerated ? `Challan ${selected.challanNumber}` : 'Amount'}
                </div>
                <div style={{ fontSize: 30, fontWeight: 700, fontFamily: C.mono, color: C.teal }}>
                  {fmtMoney(selected.chargedAmount ?? selected.quotedAmount)}
                </div>
                <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>
                  {selected.paymentReceived
                    ? 'Already collected'
                    : selected.chargedAmount != null
                      ? 'Amount to collect (rate locked)'
                      : 'Amount to collect from the vehicle owner'}
                </div>
              </div>
            )}

            {/* Both steps are shown so the desk can see the whole picture, but
                only this desk's own step is actionable — the other is read-only
                status. Order between them doesn't matter. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <StepRow
                label="Payment"
                done={selected.paymentReceived}
                doneText="Payment received"
                actionText={busy ? 'Saving…' : 'Mark Payment Received'}
                onClick={markPaid}
                busy={busy}
                color={C.green}
                readOnly={!isPaymentDesk || isBlocked(selected)}
                readOnlyText={isBlocked(selected)
                  ? 'Cannot take payment until the weighing is on record'
                  : 'Payment still pending — handled at the payment counter'}
              />
              <StepRow
                label="Challan"
                done={selected.challanGenerated}
                doneText={`Challan ${selected.challanNumber || ''} recorded`}
                actionText={busy ? 'Saving…' : 'Mark Challan Generated'}
                onClick={generateChallan}
                busy={busy}
                color={C.teal}
                readOnly={isPaymentDesk || isBlocked(selected)}
                readOnlyText={isBlocked(selected)
                  ? 'Cannot record a challan until the weighing is on record'
                  : 'Challan still pending — handled at the challan counter'}
              />
            </div>

            {!selected.paymentReceived && !selected.challanGenerated && (
              <div style={{ fontSize: 12, color: C.faint, marginTop: 14, textAlign: 'center' }}>
                Both steps are still outstanding — the vehicle moves to the exit gate once both are complete.
              </div>
            )}
          </div>
        )}

        {selected && (
          <button onClick={clearSelection} style={{
            width: '100%', background: 'none', border: 'none',
            color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.08em',
          }}>CLEAR SELECTION</button>
        )}
      </div>
    </div>
  );
}
